"use client";

import { useRef } from "react";
import Image from "next/image";
import FluidMenu from "./FluidMenu";
import { TransitionLink, useRouteTransition } from "./RouteTransition";
import usePortfolioMotion from "./usePortfolioMotion";
import "./portfolio.css";
import "./experience.css";
import "./refinements.css";
import "./history.css";

const versions = [
  {
    number: "03",
    title: "A little less ordinary.",
    caption: "The current chapter",
    description:
      "More motion. More personality. A closer look at the work and the person behind it.",
    href: "/",
    image: "/history/version-3.jpg",
    action: "Explore this version",
    tag: "CURRENT",
  },
  {
    number: "02",
    title: "No limits. Just creativity.",
    caption: "The previous chapter",
    description:
      "The familiar portfolio: immersive backgrounds, a floating navigation, and the projects that shaped my approach.",
    href: "/history/v2",
    image: "/history/version-2.jpg",
    action: "Load version 02",
    tag: "ARCHIVED",
  },
  {
    number: "01",
    title: "Where it all started.",
    caption: "The first chapter",
    description:
      "My first portfolio. A small experiment that became the start of everything that followed.",
    href: "https://sahidemon.netlify.app/",
    image: "/history/version-1.jpg",
    action: "Load version 01",
    tag: "THE ORIGINAL",
  },
];

export default function PortfolioHistory() {
  const root = useRef<HTMLDivElement>(null);
  const { phase } = useRouteTransition();
  const arrivedViaBubble = useRef(phase !== "idle");
  usePortfolioMotion(
    root,
    phase === "idle" || phase === "revealing",
    !arrivedViaBubble.current
  );

  return (
    <div ref={root} className="portfolio history-page has-fluid-menu">
      <FluidMenu />
      <div className="scroll-progress" aria-hidden="true" />
      <header className="site-header">
        <TransitionLink
          href="/"
          className="wordmark"
          aria-label="Sahidemon home"
        >
          sahi<span>demon</span>
          <i>®</i>
        </TransitionLink>
        <TransitionLink href="/" className="case-back magnetic">
          ← Current portfolio
        </TransitionLink>
        <a href="mailto:contact@sahiya.lk" className="header-contact">
          Let’s talk ↗
        </a>
      </header>
      <main>
        <section className="history-intro section-pad">
          <div className="section-label hero-enter">
            <span>THE PORTFOLIO ARCHIVE</span>
            <span>01 → 02 → 03</span>
          </div>
          <h1 className="hero-enter">
            Different versions.
            <br />
            <span className="serif">Same curiosity.</span>
          </h1>
          <div className="history-intro-bottom hero-enter">
            <p>
              Every portfolio is a little time capsule.
              <br />
              Take a look at how mine has evolved.
            </p>
            <span>THREE CHAPTERS. STILL EXPLORING.</span>
          </div>
        </section>
        <section
          className="history-versions section-pad"
          aria-label="Portfolio versions"
        >
          {versions.map((version) => {
            const content = (
              <>
                <div className="history-card-meta">
                  <span>VERSION / {version.number}</span>
                  <span>{version.tag}</span>
                </div>
                <div className="history-image">
                  <Image
                    src={version.image}
                    alt={`Portfolio version ${version.number} homepage`}
                    fill
                    sizes="(max-width: 768px) 88vw, 80vw"
                  />
                  <span className="history-view">
                    {version.number}
                    <span aria-hidden="true">↗</span>
                  </span>
                </div>
                <div className="history-card-copy">
                  <div>
                    <span>{version.caption}</span>
                    <h2>{version.title}</h2>
                  </div>
                  <div>
                    <p>{version.description}</p>
                    <span className="history-card-action">
                      {version.action} <span aria-hidden="true">↗</span>
                    </span>
                  </div>
                </div>
              </>
            );
            return (
              <article className="history-card reveal" key={version.number}>
                {version.number === "01" ? (
                  <a
                    href={version.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {content}
                    <span className="sr-only"> (opens in a new tab)</span>
                  </a>
                ) : (
                  <TransitionLink href={version.href}>{content}</TransitionLink>
                )}
              </article>
            );
          })}
        </section>
      </main>
      <footer className="site-footer">
        <TransitionLink href="/">← Back to the current chapter</TransitionLink>
        <span>SAHINDU GAYANUKA / {new Date().getFullYear()}</span>
      </footer>
    </div>
  );
}
